import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { groupSpacingAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/groupSpacing'
import { moduleSpacingAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/moduleSpacing'
import { rackingTypeAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/rackingType'
import { useTranslate } from 'ra-core'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { StudioSystemType } from 'types/global'
import { MountingID, MountingSystemType, NativeMountingID } from 'types/mounting'
import { useFeatureFlag } from 'util/featureFlags'
import { parseIntegrationJson } from 'util/misc'
import { nativeMountingIDs } from '../nativeMounting/constants'
import { syncGroundClearanceToPanelSettings } from '../nativeMounting/Inputs'
import { fastensolOnApply } from '../nativeMounting/mounting_systems/FastensolDefaults'
import { novaOnApply } from '../nativeMounting/mounting_systems/Nova/NovaHooks'
import { getSolarNestRidgeValleySpacingMeters } from '../nativeMounting/mounting_systems/solarNestRidgeValleySpacing'
import {
  SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES,
  SOLARNEST_REQUIRED_TILT_DEGREES,
} from '../nativeMounting/mounting_systems/solarNestWindSnowMax'
import { fastRackOnApply } from '../nativeMounting/mounting_systems/FastRack/FastRackHooks'
import { solarPortOnApply } from '../nativeMounting/mounting_systems/SolarPort/SolarPortHooks'
import { INCOMPATIBLE_MOUNTING_SYSTEMS } from '../nativeMounting/mountingSystems'
import { getRoofName, setRelativeTiltForModuleGrids } from '../nativeMounting/utils'
import {
  areVanDerValkSuitabilityModuleSpecsEqual,
  getVanDerValkSuitabilityModuleSpecs,
} from '../vandervalk/getVanDerValkSuitabilityPayload'
import { getVanDerValkPitchedCladdingForRoofType } from '../vandervalk/pitchedCladding'
import { syncVanDerValkPanelLayoutFromSelectedSuitability } from '../vandervalk/syncVanDerValkPanelLayoutFromSuitability'
import type { VanDerValkSuitabilityModuleSpecs } from '../vandervalk/types'
import { vanDerValkOnApply } from '../vandervalk/vanDerValkOnApply'
import { mountingSystems } from './MountingSystemCapsule'

const useApplyMountingSystem = () => {
  const orgId = useSelector(authSelectors.getOrgId)
  const countryIso2 = useSelector(orgSelectors.getOrgIso2)
  const supplierPreferences = useSelector(orgSelectors.getOrgSupplierPreferences)
  const enabledDistributors = useSelector(orgSelectors.getEnabledHardwareDistributorFilterKeys)
  const supplierPreferenceEnabled = useFeatureFlag('supplier_preference_settings', 'on')
  const translate = useTranslate()

  const handleApplyMountingSystem = useCallback(
    ({
      mountingId,
      system,
      mountingType,
    }: {
      mountingId: MountingID | undefined
      system: StudioSystemType
      mountingType?: MountingSystemType
    }): void => {
      const previousMountingId = system.mounting
      system.mounting = mountingId
      var integration_json = system.integration_json || {}

      mountingSystems.forEach((item) => {
        var mountingSystem = item.id
        if (mountingSystem === mountingId) {
          if (!integration_json[mountingId]) {
            integration_json[mountingId] = {}
          }
          // Do not auto-clear, we may keep old settings even after we switch to something else
          // }else{
          //   if(system.integration_json[mountingSystem]){
          //     delete system.integration_json[mountingSystem]
          //   }
        }
      })

      let logViridianSubmitted = false
      if (mountingId === 'viridian') {
        const newIntegration = system?.integration_json?.viridian?.enabled === undefined
        if (newIntegration) {
          // TODO: This doesn't seems wrong to me, can we either update event name or track it at right place
          logAmplitudeEvent('integrated_racking_dialog_opened', { integration: 'Viridian' })
          logViridianSubmitted = true
        }
      }

      if (mountingId === 'unirac') {
        if (!integration_json[mountingId]) {
          integration_json[mountingId] = {}
        }
        integration_json[mountingId]!.enabled = true
      }

      if (mountingId === 'gseInRoof') {
        if (!integration_json[mountingId]) {
          integration_json[mountingId] = {}
        }
        integration_json[mountingId]!.rearSupportBarIncluded = false
      }

      if (mountingId !== 'solarNestRooftop' && mountingId !== 'solarNestGroundMount') {
        if (!integration_json.solarNestRooftop) {
          integration_json.solarNestRooftop = {}
        }
        integration_json.solarNestRooftop = {
          ...integration_json.solarNestRooftop,
          tiltOption: 'eastWestTilt',
        }
      }

      if (mountingId === 'solarNestRooftop') {
        if (!integration_json.solarNestRooftop) {
          integration_json.solarNestRooftop = {}
        }
        if (integration_json.solarNestRooftop.tiltOption === undefined) {
          integration_json.solarNestRooftop = {
            ...integration_json.solarNestRooftop,
            tiltOption: 'eastWestTilt',
          }
        }
        if (integration_json.solarNestRooftop.tiltOption === 'eastWestTilt') {
          system.moduleGrids().forEach((moduleGrid) => {
            rackingTypeAccessorConfig.set({ moduleGrid, value: 'DUAL_TILT_RACK' })
          })
          setRelativeTiltForModuleGrids(system, SOLARNEST_REQUIRED_TILT_DEGREES)
        }
        system.moduleGrids().forEach((moduleGrid) => {
          if (moduleGrid.moduleLayout() !== 'landscape') {
            window.editor.execute(
              new window.RedrawModulesCommand(moduleGrid, 'moduleLayout', 'landscape', moduleGrid.moduleLayout())
            )
          }
        })
      }

      if (mountingId === 'solarNestGroundMount') {
        integration_json.solarNestGroundMount = {
          groundClearance: SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES,
        }
        syncGroundClearanceToPanelSettings(system, SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES)
        if (integration_json.solarNestGroundMount.tiltOption === undefined) {
          integration_json.solarNestGroundMount = {
            ...integration_json.solarNestGroundMount,
            tiltOption: 'eastWestTilt',
          }
        }
        if (integration_json.solarNestGroundMount.tiltOption === 'eastWestTilt') {
          system.moduleGrids().forEach((moduleGrid) => {
            rackingTypeAccessorConfig.set({ moduleGrid, value: 'DUAL_TILT_RACK' })
          })
          setRelativeTiltForModuleGrids(system, SOLARNEST_REQUIRED_TILT_DEGREES)
        }
        system.moduleGrids().forEach((moduleGrid) => {
          if (moduleGrid.moduleLayout() !== 'landscape') {
            window.editor.execute(
              new window.RedrawModulesCommand(moduleGrid, 'moduleLayout', 'landscape', moduleGrid.moduleLayout())
            )
          }
        })
      }

      var commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
      // Sets the last selected module type to the first drawn module type
      // if multiple panels is incompatible with mounting system then we should just use the first drawn module type
      if (mountingId && INCOMPATIBLE_MOUNTING_SYSTEMS['multiple-panels'].includes(mountingId)) {
        const lastSelectedModuleTypeId = window.editor.selectedSystem.getLastSelectedModuleType().id
        const firstDrawnModuleTypeId = window.editor.selectedSystem.moduleType().id
        window.editor.execute(
          new window.UpdateElectricalsCommand(
            window.editor.selectedSystem,
            'setModuleTypeByModuleId',
            firstDrawnModuleTypeId,
            lastSelectedModuleTypeId
          )
        )
      }

      if (mountingId === 'vanDerValk') {
        const isPitched = mountingType === 'onRoof'
        const base = (parseIntegrationJson(integration_json) as Record<string, unknown> | null) || integration_json
        const prevVdv = (base.vanDerValk as Record<string, unknown> | undefined) || {}
        const rawResult = prevVdv.suitabilityResult
        const prevSpecs = prevVdv.suitabilityModuleSpecs as VanDerValkSuitabilityModuleSpecs | null | undefined
        const nextSpecs = getVanDerValkSuitabilityModuleSpecs(system)

        // Pitched-roof cladding is derived from the project roof type (no user dropdown); cached
        // pitched results are only valid for that cladding. Flat-roof does not use cladding, so the
        // expected value is null there. Comparing against the stored value also invalidates results
        // carried over from the other mounting type when switching between flat and pitched.
        const expectedCladding = isPitched
          ? getVanDerValkPitchedCladdingForRoofType(getRoofName(window.projectForm?.getState()?.values?.roof_type))
          : null
        const prevCladding = (prevVdv.suitabilityCladdingType as string | null | undefined) ?? null

        const specsChanged =
          prevSpecs != null && (nextSpecs == null || !areVanDerValkSuitabilityModuleSpecsEqual(prevSpecs, nextSpecs))
        const claddingChanged = prevCladding !== expectedCladding
        // ``[]`` means API completed with zero rows; invalidate with null so the no-results warning
        // does not flash before the next suitability fetch finishes.
        const hasCachedSuitabilityResult = rawResult != null || prevVdv.selectedSuitability != null

        let nextVdv: Record<string, unknown> = { ...prevVdv }
        if (hasCachedSuitabilityResult && (specsChanged || claddingChanged)) {
          nextVdv = {
            ...nextVdv,
            suitabilityResult: null,
            selectedSuitability: null,
            suitabilitySeamType: null,
            suitabilityCladdingOptionsKey: null,
            suitabilityGroupingVersion: null,
          }
        }
        nextVdv = { ...nextVdv, suitabilityModuleSpecs: nextSpecs, suitabilityCladdingType: expectedCladding }
        integration_json = { ...base, vanDerValk: nextVdv }
      }

      window.editor.execute(new window.SetValueCommand(system, 'mounting', mountingId, commandUUID))
      window.editor.execute(new window.SetValueCommand(system, 'integration_json', integration_json, commandUUID))
      if (mountingType) {
        window.editor.execute(new window.SetValueCommand(system, 'mounting_type', mountingType, commandUUID))
      }

      if (mountingId === 'vanDerValk') {
        syncVanDerValkPanelLayoutFromSelectedSuitability(system)
        vanDerValkOnApply(system)
      }

      if (logViridianSubmitted) {
        logAmplitudeEvent('integrated_racking_dialog_submitted', { integration: 'Viridian' })
      }

      if (mountingId) {
        const hasNoIntegration = system?.integration_json?.[mountingId]?.enabled === undefined
        if (nativeMountingIDs.includes(mountingId as NativeMountingID) && hasNoIntegration) {
          logAmplitudeEvent('selected_native_mounting_system', { integration: mountingId })
        }
      } else if (previousMountingId) {
        logAmplitudeEvent('deselected_mounting_system', {
          integration: previousMountingId,
          systemId: system.uuid,
        })
      }

      window.editor.signals.objectChanged.dispatch(system, 'mounting')

      if (mountingId === 'nova') {
        novaOnApply(system)
      }

      if (mountingId === 'fastRack') {
        fastRackOnApply(system)
      }

      // Apply ridge/valley after commands and dispatch so recalc (e.g. setPanelSpacing) does not overwrite
      const tiltOptionRooftop = system.integration_json?.solarNestRooftop?.tiltOption
      const tiltOptionGroundMount = system.integration_json?.solarNestGroundMount?.tiltOption
      const tiltOption = tiltOptionRooftop || tiltOptionGroundMount
      if (
        (mountingId === 'solarNestRooftop' || mountingId === 'solarNestGroundMount') &&
        tiltOption === 'eastWestTilt'
      ) {
        const sys = system
        const firstGrid = sys.moduleGrids()[0]
        const moduleType = firstGrid?.moduleType?.()
        const frameWidthMm = (moduleType as { thickness?: number } | undefined)?.thickness ?? 35
        const { ridgeM, valleyM } = getSolarNestRidgeValleySpacingMeters(frameWidthMm)
        sys.moduleGrids().forEach((moduleGrid) => {
          const currentModule = moduleSpacingAccessorConfig.get(moduleGrid)
          const currentGroup = groupSpacingAccessorConfig.get(moduleGrid)
          moduleSpacingAccessorConfig.set({ moduleGrid, value: [currentModule[0], ridgeM] })
          groupSpacingAccessorConfig.set({ moduleGrid, value: [currentGroup[0], valleyM] })
        })
        window.Designer?.showNotification(
          window.translate(
            'Dual tilt set to %{degrees} degrees and module spacing adjusted to be compatible with selected mounting system.',
            { degrees: SOLARNEST_REQUIRED_TILT_DEGREES }
          ),
          'info'
        )
      }

      if (mountingId === 'solarPortGroundMount') {
        solarPortOnApply(system)
      }

      if (mountingId === 'fastensol' && orgId !== undefined) {
        void fastensolOnApply(system, {
          orgId,
          countryIso2,
          supplierPreferences,
          enabledDistributors,
          supplierPreferenceEnabled,
          translate,
        })
      }
    },
    [orgId, countryIso2, supplierPreferences, enabledDistributors, supplierPreferenceEnabled, translate]
  )

  return handleApplyMountingSystem
}

export default useApplyMountingSystem
